/*
 * ============================================================================
 *  Currently Studio — Wishlist (favoris)
 *  Stockage : localStorage uniquement (clé `cs_wishlist`)
 *  Format   : { v: 1, items: [{ id, handle, addedAt }] }
 *
 *  Composants exposés :
 *   - window.wishlist           : singleton manager (add/remove/toggle/has/getAll/clear/count)
 *   - <wishlist-button>         : bouton cœur (cards + fiche produit)
 *   - <wishlist-count>          : badge compteur (header)
 *   - <wishlist-grid>           : grille de la page favoris
 *
 *  Évènement global : `wishlist:change` (window) — payload { items, count }
 * ============================================================================
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'cs_wishlist';
  var SCHEMA_VERSION = 1;
  var CHANGE_EVENT = 'wishlist:change';

  /* ------------------------------------------------------------------ */
  /*  Manager                                                            */
  /* ------------------------------------------------------------------ */
  function WishlistManager() {
    this._items = this._load();
    this._bindCrossTab();
  }

  WishlistManager.prototype._load = function () {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      // Tolérant aux anciens formats : si c'est déjà un tableau, on prend tel quel
      if (Array.isArray(parsed)) return this._sanitize(parsed);
      if (parsed && Array.isArray(parsed.items)) return this._sanitize(parsed.items);
      return [];
    } catch (e) {
      return [];
    }
  };

  WishlistManager.prototype._sanitize = function (arr) {
    var seen = {};
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var it = arr[i];
      if (!it || it.id == null || !it.handle) continue;
      var id = String(it.id);
      if (seen[id]) continue;
      seen[id] = true;
      out.push({
        id: id,
        handle: String(it.handle),
        addedAt: typeof it.addedAt === 'number' ? it.addedAt : Date.now()
      });
    }
    return out;
  };

  WishlistManager.prototype._save = function () {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ v: SCHEMA_VERSION, items: this._items })
      );
    } catch (e) {
      // localStorage plein ou désactivé : fail silently
    }
  };

  WishlistManager.prototype._emit = function (changedId) {
    var detail = {
      items: this._items.slice(),
      count: this._items.length,
      changedId: changedId || null
    };
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: detail }));
  };

  WishlistManager.prototype._bindCrossTab = function () {
    var self = this;
    window.addEventListener('storage', function (e) {
      if (e.key !== STORAGE_KEY) return;
      self._items = self._load();
      self._emit(null);
    });
  };

  WishlistManager.prototype.has = function (productId) {
    if (productId == null) return false;
    var id = String(productId);
    for (var i = 0; i < this._items.length; i++) {
      if (this._items[i].id === id) return true;
    }
    return false;
  };

  WishlistManager.prototype.add = function (productId, handle) {
    if (productId == null || !handle) return false;
    if (this.has(productId)) return false;
    this._items.push({
      id: String(productId),
      handle: String(handle),
      addedAt: Date.now()
    });
    this._save();
    this._emit(String(productId));
    return true;
  };

  WishlistManager.prototype.remove = function (productId) {
    if (productId == null) return false;
    var id = String(productId);
    var before = this._items.length;
    this._items = this._items.filter(function (it) {
      return it.id !== id;
    });
    if (this._items.length === before) return false;
    this._save();
    this._emit(id);
    return true;
  };

  WishlistManager.prototype.toggle = function (productId, handle) {
    if (this.has(productId)) {
      this.remove(productId);
      return false; // n'est plus en favori
    }
    this.add(productId, handle);
    return true; // est en favori
  };

  WishlistManager.prototype.clear = function () {
    if (this._items.length === 0) return;
    this._items = [];
    this._save();
    this._emit(null);
  };

  WishlistManager.prototype.getAll = function () {
    // Retour trié par date d'ajout, plus récent en premier
    return this._items
      .slice()
      .sort(function (a, b) {
        return b.addedAt - a.addedAt;
      });
  };

  WishlistManager.prototype.count = function () {
    return this._items.length;
  };

  // Retire silencieusement des items (utilisé par le cleanup auto quand un produit est dépublié)
  WishlistManager.prototype.removeMany = function (ids) {
    if (!ids || !ids.length) return;
    var toRemove = {};
    for (var i = 0; i < ids.length; i++) toRemove[String(ids[i])] = true;
    var before = this._items.length;
    this._items = this._items.filter(function (it) {
      return !toRemove[it.id];
    });
    if (this._items.length !== before) {
      this._save();
      this._emit(null);
    }
  };

  // Singleton global
  window.wishlist = new WishlistManager();

  /* ------------------------------------------------------------------ */
  /*  Web Component : <wishlist-button>                                  */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class extends HTMLElement {
        constructor() {
          super();
          this._onClick = this._onClick.bind(this);
          this._onChange = this._onChange.bind(this);
        }

        connectedCallback() {
          this.button = this.querySelector('button');
          this.productId = this.getAttribute('data-product-id');
          this.productHandle = this.getAttribute('data-product-handle');
          if (!this.button || !this.productId || !this.productHandle) return;
          this.button.addEventListener('click', this._onClick);
          window.addEventListener(CHANGE_EVENT, this._onChange);
          this._render();
        }

        disconnectedCallback() {
          if (this.button) this.button.removeEventListener('click', this._onClick);
          window.removeEventListener(CHANGE_EVENT, this._onChange);
        }

        _onClick(e) {
          e.preventDefault();
          e.stopPropagation();
          var nowFav = window.wishlist.toggle(this.productId, this.productHandle);
          // anim "pulse"
          this.button.classList.remove('is-pulsing');
          // force reflow pour pouvoir relancer l'anim
          // eslint-disable-next-line no-unused-expressions
          this.button.offsetWidth;
          this.button.classList.add('is-pulsing');

          // Le clic donne le focus au bouton, ce qui maintient :focus-within
          // sur la card et bloque la disparition du quick-buy quand la souris part.
          // On retire le focus pour laisser le thème gérer normalement les états hover.
          var btn = this.button;
          var self = this;
          requestAnimationFrame(function () {
            try { btn.blur(); } catch (err) { /* noop */ }
            // Si la souris n'est plus au-dessus de la card, on simule un mouseleave
            // pour que le thème referme le quick-buy / retire la classe --hovered.
            var card = self.closest('.product-grid-item__image, .product-grid-item');
            if (card && !card.matches(':hover')) {
              card.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
            }
          });
        }

        _onChange(e) {
          // Re-render uniquement si ça concerne ce produit ou un reset global
          if (!e || !e.detail) return this._render();
          if (e.detail.changedId === null || e.detail.changedId === this.productId) {
            this._render();
          }
        }

        _render() {
          var isFav = window.wishlist.has(this.productId);
          this.classList.toggle('is-active', isFav);
          this.button.setAttribute('aria-pressed', isFav ? 'true' : 'false');
          var labelOn = this.getAttribute('data-label-on') || 'Retirer des favoris';
          var labelOff = this.getAttribute('data-label-off') || 'Ajouter aux favoris';
          this.button.setAttribute('aria-label', isFav ? labelOn : labelOff);
          this.button.setAttribute('title', isFav ? labelOn : labelOff);
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Web Component : <wishlist-count> (badge header)                    */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class extends HTMLElement {
        constructor() {
          super();
          this._onChange = this._onChange.bind(this);
        }

        connectedCallback() {
          this._render();
          window.addEventListener(CHANGE_EVENT, this._onChange);
        }

        disconnectedCallback() {
          window.removeEventListener(CHANGE_EVENT, this._onChange);
        }

        _onChange() {
          this._render();
        }

        _render() {
          var count = window.wishlist.count();
          this.textContent = count > 99 ? '99+' : String(count);
          this.classList.toggle('is-empty', count === 0);
          this.setAttribute('data-count', count);
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Web Component : <wishlist-grid> (page favoris)                     */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('wishlist-grid')) {
    var SORT_STORAGE_KEY = 'cs_wishlist_sort';
    var DEFAULT_SORT = 'added-desc';

    customElements.define(
      'wishlist-grid',
      class extends HTMLElement {
        constructor() {
          super();
          this._onChange = this._onChange.bind(this);
          this._onSortChange = this._onSortChange.bind(this);
          this._renderInProgress = false;
          this._cardsCache = {}; // id -> { node, item, price, title }
          this._currentSort = DEFAULT_SORT;
        }

        connectedCallback() {
          this.gridEl = this.querySelector('[data-wishlist-grid-items]');
          this.emptyEl = this.querySelector('[data-wishlist-empty]');
          this.headerEl = this.querySelector('[data-wishlist-header]');
          this.stickyBarEl = this.querySelector('[data-wishlist-sticky-bar]');
          this.countLabelEl = this.querySelector('[data-wishlist-count-label]');
          this.countWrapEl = this.querySelector('[data-products-count]');
          this.sortInputs = this.querySelectorAll('[data-input-sort]');
          this.sortToggleBtn = this.querySelector('[data-sort-toggle]');
          this.sortOptionsEl = this.querySelector('[data-collection-sort-options]');

          // Récupère les chaînes de pluralisation depuis le DOM (rendues par Liquid)
          // pour pouvoir formater le compteur côté client. Le placeholder __COUNT__
          // est injecté côté Liquid (count: '__COUNT__') puis substitué ici.
          this._countLabelTpl = {
            one: this.getAttribute('data-count-label-one') || '',
            other: this.getAttribute('data-count-label-other') || ''
          };

          // Restore le tri sauvegardé
          try {
            var saved = window.localStorage.getItem(SORT_STORAGE_KEY);
            if (saved) this._currentSort = saved;
          } catch (e) {}
          // Synchronise les radios avec le sort actif
          for (var i = 0; i < this.sortInputs.length; i++) {
            var input = this.sortInputs[i];
            input.checked = input.value === this._currentSort;
            input.addEventListener('change', this._onSortChange);
          }

          // Toggle dropdown du sort (normalement géré par <collection-filters>)
          this._onSortToggleClick = this._onSortToggleClick.bind(this);
          this._onBodyClick = this._onBodyClick.bind(this);
          if (this.sortToggleBtn) {
            this.sortToggleBtn.addEventListener('click', this._onSortToggleClick);
            document.addEventListener('click', this._onBodyClick);
          }

          window.addEventListener(CHANGE_EVENT, this._onChange);
          this._render();
        }

        disconnectedCallback() {
          if (this.sortInputs) {
            for (var i = 0; i < this.sortInputs.length; i++) {
              this.sortInputs[i].removeEventListener('change', this._onSortChange);
            }
          }
          if (this.sortToggleBtn) {
            this.sortToggleBtn.removeEventListener('click', this._onSortToggleClick);
            document.removeEventListener('click', this._onBodyClick);
          }
          window.removeEventListener(CHANGE_EVENT, this._onChange);
        }

        _onSortToggleClick(e) {
          e.preventDefault();
          if (this.sortOptionsEl) {
            this.sortOptionsEl.classList.toggle('collection__sort__option-wrapper--visible');
          }
        }

        _onBodyClick(e) {
          if (!this.sortOptionsEl || !this.sortToggleBtn) return;
          if (!this.sortOptionsEl.classList.contains('collection__sort__option-wrapper--visible')) return;
          if (this.sortToggleBtn.contains(e.target)) return;
          this.sortOptionsEl.classList.remove('collection__sort__option-wrapper--visible');
        }

        _onChange(e) {
          // Si le change vient d'un retrait depuis la page elle-même, on retire le node
          // sans re-fetcher tout le reste. Sinon (clear, ajout depuis ailleurs), on re-render.
          if (!e || !e.detail) return this._render();
          if (e.detail.changedId && e.detail.count < this._lastCount) {
            this._removeCard(e.detail.changedId);
          } else {
            this._render();
          }
        }

        _onSortChange(e) {
          var val = e.target.value;
          if (!val || val === this._currentSort) return;
          this._currentSort = val;
          try { window.localStorage.setItem(SORT_STORAGE_KEY, val); } catch (err) {}
          // Re-arranger les cards déjà chargées sans refetch
          this._reorderCards();
        }

        _removeCard(productId) {
          if (!this.gridEl) return;
          var card = this.gridEl.querySelector('[data-wishlist-card="' + productId + '"]');
          if (card) {
            card.classList.add('is-removing');
            setTimeout(function () {
              if (card.parentNode) card.parentNode.removeChild(card);
            }, 250);
          }
          delete this._cardsCache[productId];
          this._lastCount = window.wishlist.count();
          this._updateHeader();
          if (this._lastCount === 0) {
            // Petit délai pour laisser l'anim de retrait se finir
            var self = this;
            setTimeout(function () {
              self._showEmpty();
            }, 260);
          }
        }

        _updateHeader() {
          var count = window.wishlist.count();
          if (this.countWrapEl) this.countWrapEl.setAttribute('data-products-count', count);
          if (this.countLabelEl) {
            var tpl = count === 1 ? this._countLabelTpl.one : this._countLabelTpl.other;
            this.countLabelEl.textContent = (tpl || '').replace(/__COUNT__/g, count);
          }
        }

        _showEmpty() {
          if (this.gridEl) this.gridEl.innerHTML = '';
          if (this.emptyEl) this.emptyEl.hidden = false;
          if (this.headerEl) this.headerEl.hidden = true;
          if (this.stickyBarEl) this.stickyBarEl.hidden = true;
          this._cardsCache = {};
        }

        _showFilled() {
          if (this.emptyEl) this.emptyEl.hidden = true;
          if (this.headerEl) this.headerEl.hidden = false;
          if (this.stickyBarEl) this.stickyBarEl.hidden = false;
        }

        // Extrait des données depuis la card pour permettre le tri côté client
        _extractCardData(node) {
          var titleEl = node.querySelector('.product-grid-item__title');
          var priceEl = node.querySelector('.price .money, .product-grid-item__price .money, [data-price]');
          var title = titleEl ? (titleEl.textContent || '').trim().toLowerCase() : '';
          var price = 0;
          if (priceEl) {
            var raw = (priceEl.getAttribute('data-price') || priceEl.textContent || '').trim();
            // Garde uniquement chiffres + virgule/point
            var num = raw.replace(/[^0-9.,]/g, '').replace(',', '.');
            price = parseFloat(num) || 0;
          }
          return { title: title, price: price };
        }

        // Trie un tableau de cards selon le sort actif
        _sortItems(arr) {
          var sort = this._currentSort;
          var copy = arr.slice();
          switch (sort) {
            case 'added-asc':
              copy.sort(function (a, b) { return a.item.addedAt - b.item.addedAt; });
              break;
            case 'title-asc':
              copy.sort(function (a, b) {
                if (a.title < b.title) return -1;
                if (a.title > b.title) return 1;
                return 0;
              });
              break;
            case 'price-asc':
              copy.sort(function (a, b) { return a.price - b.price; });
              break;
            case 'price-desc':
              copy.sort(function (a, b) { return b.price - a.price; });
              break;
            case 'added-desc':
            default:
              copy.sort(function (a, b) { return b.item.addedAt - a.item.addedAt; });
          }
          return copy;
        }

        // Réordonne les cards déjà dans le DOM sans refetch
        _reorderCards() {
          if (!this.gridEl) return;
          var entries = [];
          for (var id in this._cardsCache) {
            if (Object.prototype.hasOwnProperty.call(this._cardsCache, id)) {
              entries.push(this._cardsCache[id]);
            }
          }
          var sorted = this._sortItems(entries);
          // Réinjection avec animation cascade
          this.gridEl.innerHTML = '';
          for (var i = 0; i < sorted.length; i++) {
            var node = sorted[i].node;
            // Reset l'animation pour qu'elle rejoue
            node.classList.remove('aos-animate');
            this.gridEl.appendChild(node);
          }
          this._triggerCascadeAnimation(sorted.length);
        }

        // Déclenche l'animation aos-animate avec un délai en cascade
        _triggerCascadeAnimation(count) {
          if (!this.gridEl) return;
          var cards = this.gridEl.children;
          var STEP_MS = 60;     // délai entre cards
          var BASE_MS = 30;     // delai initial
          for (var i = 0; i < cards.length; i++) {
            (function (el, idx) {
              setTimeout(function () {
                el.classList.add('aos-animate');
                // Animer aussi les enfants qui ont data-aos
                var nested = el.querySelectorAll('[data-aos]:not(.aos-animate)');
                for (var j = 0; j < nested.length; j++) {
                  nested[j].classList.add('aos-animate');
                }
              }, BASE_MS + idx * STEP_MS);
            })(cards[i], i);
          }
        }

        async _render() {
          if (this._renderInProgress) return;
          this._renderInProgress = true;

          var items = window.wishlist.getAll();
          this._lastCount = items.length;

          if (items.length === 0) {
            this._showEmpty();
            this._updateHeader();
            this._renderInProgress = false;
            return;
          }

          this._showFilled();
          this._updateHeader();

          // Indicateur de chargement
          if (this.gridEl) {
            this.gridEl.setAttribute('aria-busy', 'true');
          }

          var section = this.getAttribute('data-card-section') || 'wishlist-card';
          var results = await Promise.all(
            items.map(function (it) {
              // Section Rendering API : on demande à Shopify de rendre la section
              // `wishlist-card` dans le contexte de la page produit du favori.
              // Avantage : l'objet `product` global est correctement injecté, donc
              // `product-grid-item` se rend exactement comme dans une collection.
              var url = '/products/' + encodeURIComponent(it.handle) +
                        '?section_id=' + encodeURIComponent(section);
              return fetch(url, { credentials: 'same-origin' })
                .then(function (r) {
                  if (!r.ok) return { ok: false, item: it };
                  return r.text().then(function (html) {
                    // Si la section a renvoyé un marker "not found", on retire l'item
                    if (html.indexOf('data-wishlist-not-found') !== -1) {
                      return { ok: false, item: it };
                    }
                    return { ok: true, item: it, html: html };
                  });
                })
                .catch(function () {
                  return { ok: false, item: it };
                });
            })
          );

          // Cleanup : produits 404 / dépubliés
          var deadIds = results
            .filter(function (r) {
              return !r.ok;
            })
            .map(function (r) {
              return r.item.id;
            });
          if (deadIds.length) window.wishlist.removeMany(deadIds);

          // Build cache de cards parsées
          var self = this;
          this._cardsCache = {};
          var entries = [];
          results
            .filter(function (r) { return r.ok; })
            .forEach(function (r) {
              var wrapper = document.createElement('div');
              wrapper.innerHTML = r.html.trim();
              var node = wrapper.firstElementChild;
              if (node && node.classList && node.classList.contains('shopify-section')) {
                var inner = node.querySelector('.product-grid-item') || node.firstElementChild;
                if (inner) node = inner;
              }
              if (!node) return;
              node.setAttribute('data-wishlist-card', r.item.id);
              // Force l'opacité 0 initiale pour permettre la cascade
              if (!node.classList.contains('aos-animate')) {
                // déjà à 0 via CSS data-aos, rien à faire
              }
              var data = self._extractCardData(node);
              var entry = {
                node: node,
                item: r.item,
                title: data.title,
                price: data.price
              };
              self._cardsCache[r.item.id] = entry;
              entries.push(entry);
            });

          // Tri + injection
          var sorted = this._sortItems(entries);
          if (this.gridEl) {
            this.gridEl.innerHTML = '';
            for (var i = 0; i < sorted.length; i++) {
              this.gridEl.appendChild(sorted[i].node);
            }
            this.gridEl.removeAttribute('aria-busy');
            this._triggerCascadeAnimation(sorted.length);
          }

          this._lastCount = window.wishlist.count();
          this._updateHeader();
          if (this._lastCount === 0) this._showEmpty();
          this._renderInProgress = false;
        }
      }
    );
  }
})();
